// Shared PWA install state.
//
// Chrome/Android fires `beforeinstallprompt` exactly once, whenever the browser
// decides the app is installable. We capture that event at module scope so any
// component (auto banner OR the "Install app" item in the TopbarMe dropdown)
// can call it — otherwise the second consumer would miss the event and users
// would have no way to install after dismissing the banner.
//
// iOS Safari never fires the event; we surface an instruction hint instead.

import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let captured: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e: Event) => {
        e.preventDefault();
        captured = e as BeforeInstallPromptEvent;
        listeners.forEach(fn => fn());
    });
    window.addEventListener('appinstalled', () => {
        captured = null;
        listeners.forEach(fn => fn());
    });
}

export function isStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIOS(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent)
        && !/CriOS|FxiOS/i.test(navigator.userAgent);
}

export function useInstall() {
    const [, tick] = useState(0);
    useEffect(() => {
        const fn = () => tick(t => t + 1);
        listeners.add(fn);
        return () => { listeners.delete(fn); };
    }, []);

    const standalone = isStandalone();
    const ios = isIOS();

    async function install(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
        if (!captured) return 'unavailable';
        await captured.prompt();
        const { outcome } = await captured.userChoice;
        captured = null;
        listeners.forEach(fn => fn());
        return outcome;
    }

    return {
        canPrompt: !!captured && !standalone,
        standalone,
        ios,
        install,
    };
}
